import axios from 'axios';
import { notification } from 'antd';
import { getAccessToken, getRefreshToken, getRealm, setAccessToken, setRefreshToken, clearStorage } from './storage';

const copilot = axios.create({
  baseURL: import.meta.env.VITE_APP_COPILOT,
});

let isToastDisplayed = false;

const logoutUser = () => {
  const accessToken = getAccessToken();
  if (accessToken) {
    if (!isToastDisplayed) {
      notification.error({
        message: 'Your session has expired. Please login to pick up where you left off.',
        placement: 'bottomRight',
      });
      isToastDisplayed = true;
    }
    setTimeout(() => {
      clearStorage();
      window.location.assign('/auth/login');
    }, 1000);
  }
};

copilot.interceptors.request.use((config) => {
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return config;
});

copilot.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle token expiration
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const accessToken = await refreshToken();
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        return copilot(originalRequest);
      } catch (err) {
        logoutUser();
      }
    }

    // Handle server errors
    if (error.response?.status >= 500) {
      notification.error({
        message: 'Service Temporarily Unavailable. Please try again later.',
        placement: 'bottomRight',
      });
    }

    return Promise.reject(error);
  }
);

export const refreshToken = async () => {
  const realm = getRealm();
  const refresh_token = getRefreshToken();

  try {
    const response = await copilot.post('/v1/token/refresh', {
      refreshToken: refresh_token,
      realm,
    });
    const { access_token, refresh_token: newRefresh } = response.data;
    setAccessToken(access_token);
    setRefreshToken(newRefresh);
    return access_token;
  } catch (err) {
    throw err;
  }
};

export const login = async (payload) => {
  try {
    const response = await copilot.post('/v1/user/login', payload);
    return response;
  } catch (e) {
    throw {
      response: {
        error: true,
        data: (e?.response && e.response?.data?.error) || 'Something went wrong.',
        message: (e?.response && e.response?.data?.message) || 'Something went wrong.',
        code: e?.response?.status || 404,
      },
    };
  }
};

export const forgotPassword = async (payload) => {
  try {
    const response = await copilot.post('/v1/user/password/forgot', payload);
    return response;
  } catch (e) {
    throw {
      response: {
        error: true,
        data: (e?.response && e.response?.data?.error) || 'Something went wrong.',
        message: (e?.response && e.response?.data?.message) || 'Something went wrong.',
        code: e?.response?.status || 404,
      },
    };
  }
};

export const getXPayUsers = async (id) => {
  try {
    const response = await copilot.get(`/v1/users/realms/${id}/roles/xpay`);
    return response;
  } catch (e) {
    throw {
      response: {
        error: true,
        data: (e?.response && e.response?.data?.error) || 'Something went wrong.',
        message: (e?.response && e.response?.data?.message) || 'Something went wrong.',
        code: e?.response?.status || 404,
      },
    };
  }
};

export const createXPayUser = async (payload) => {
  try {
    const response = await copilot.post('/v1/user/create', payload);
    return response;
  } catch (e) {
    throw {
      response: {
        error: true,
        data: (e?.response && e.response?.data?.error) || 'Something went wrong.',
        message: (e?.response && e.response?.data?.message) || 'Something went wrong.',
        code: e?.response?.status || 404,
      },
    };
  }
};

export const updateXPayUser = async (payload) => {
  try {
    const response = await copilot.put('/v1/user/status', payload);
    return response;
  } catch (e) {
    throw {
      response: {
        error: true,
        data: (e?.response && e.response?.data?.error) || 'Something went wrong.',
        message: (e?.response && e.response?.data?.message) || 'Something went wrong.',
        code: e?.response?.status || 404,
      },
    };
  }
};

export const addXPayUserRole = async (userRoles) => {
  try {
    const response = await copilot.post('/v1/user/role/add', userRoles);
    return response;
  } catch (e) {
    throw {
      response: {
        error: true,
        data: (e?.response && e.response?.data?.error) || 'Something went wrong.',
        message: (e?.response && e.response?.data?.message) || 'Something went wrong.',
        code: e?.response?.status || 404,
      },
    };
  }
};

export const deleteXPayUserRole = async (deleteUserRole) => {
  try {
    const response = await copilot.delete('/v1/user/role', {
      data: deleteUserRole,
    });
    return response;
  } catch (e) {
    throw {
      response: {
        error: true,
        data: (e?.response && e.response?.data?.error) || 'Something went wrong.',
        message: (e?.response && e.response?.data?.message) || 'Something went wrong.',
        code: e?.response?.status || 404,
      },
    };
  }
};

export const resetPassword = async (payload, authToken) => {
  try {
    const response = await axios.patch(`${import.meta.env.VITE_APP_COPILOT}/v1/user/password/reset`, payload, {
      headers: {
        Authorization: authToken,
      },
    });
    return response;
  } catch (e) {
    throw {
      response: {
        error: true,
        data: (e?.response && e.response?.data?.error) || 'Something went wrong.',
        message: (e?.response && e.response?.data?.message) || 'Something went wrong.',
        code: e?.response?.status || 404,
      },
    };
  }
};


export default copilot;
